/**
 * Cloudflare Turnstile vor jedem öffentlichen Auth-Aufruf.
 *
 * WARUM: Registrieren, Mail erneut senden, Passwort vergessen und Anmelden
 * lassen sich mit dem Publishable Key von außen auslösen. Ein Skript kann so
 * Bestätigungsmails an fremde Adressen schicken (das kostet die Reputation
 * von m.99takes.com) oder Passwörter durchprobieren. Ist in Supabase unter
 * Authentication → Attack Protection das CAPTCHA mit Turnstile an, prüft der
 * Auth-Server jedes `captchaToken` selbst bei Cloudflare; die App schickt es
 * nur mit. Solange es dort aus ist, übergeht Supabase das Feld.
 *
 * EIN TOKEN GILT GENAU EINMAL. Die App bleibt nach einem Fehlversuch auf
 * derselben Seite. `ausfuehren` setzt das Widget deshalb nach jedem Aufruf
 * zurück, und der nächste Versuch bekommt einen frischen Token.
 *
 * `appearance: always`: das Feld steht sofort über dem Knopf und zeigt
 * „Verifiziere …“, solange Cloudflare prüft. Mit `interaction-only` sah man
 * 3 bis 10 s lang nichts, dann sprang die Checkbox ins Formular (19.10.2026).
 *
 * Wo es steht und wie es ausgerollt wird: apps/web/AGENTS.md, „Turnstile vor
 * der Anmeldung“, und Wurzel-AGENTS.md, „Turnstile vor den Auth-Aufrufen“.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { TURNSTILE_SITE_KEY } from './config';
import { sprache, t } from './sprache';
import { Hinweis } from './ui';

/**
 * Die `action` je Oberfläche. Cloudflare zeigt sie in der Auswertung des
 * Widgets, so lässt sich sehen, wo Prüfungen scheitern. Erlaubt sind bis zu
 * 32 Zeichen aus Buchstaben, Ziffern, `_` und `-`.
 */
export const TURNSTILE_AKTIONEN = ['signup', 'resend', 'reset', 'login', 'connect', 'code'] as const;
export type TurnstileAktion = (typeof TURNSTILE_AKTIONEN)[number];

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SKRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let laden: Promise<TurnstileApi> | null = null;

/** Lädt das Skript einmal je Seite. Scheitert es, versucht der nächste Aufruf neu. */
function ladeTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  laden ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SKRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile fehlt')));
    s.onerror = () => {
      laden = null;
      s.remove();
      reject(new Error('Turnstile ließ sich nicht laden'));
    };
    document.head.appendChild(s);
  });
  return laden;
}

export interface Pruefung {
  /** Das Feld fürs Formular, direkt über den Knopf. Meist unsichtbar. */
  feld: ReactNode;
  /** Ein Aufruf wartet auf den Token: der Knopf zeigt „Einen Moment …“. */
  wartet: boolean;
  /**
   * Führt `aufruf` mit einem frischen Token aus, sofort oder sobald einer da
   * ist. Danach wird das Widget zurückgesetzt, egal wie der Aufruf ausging.
   */
  ausfuehren: (aufruf: (captchaToken: string) => Promise<unknown>) => void;
}

/** Turnstile für eine Oberfläche. `feld` gehört ins Formular, `ausfuehren` in den Absenden-Weg. */
export function useTurnstile(aktion: TurnstileAktion): Pruefung {
  const [wartet, setWartet] = useState(false);
  const [stoerung, setStoerung] = useState(false);
  // Neu zählen baut das Widget neu auf (Knopf „Noch einmal prüfen“).
  const [runde, setRunde] = useState(0);

  const widget = useRef<string | null>(null);
  const token = useRef<string | null>(null);
  const offen = useRef<((captchaToken: string) => Promise<unknown>) | null>(null);
  const laeuft = useRef(false);

  const einloesen = useCallback(async (aufruf: (captchaToken: string) => Promise<unknown>, tok: string) => {
    token.current = null;
    laeuft.current = true;
    try {
      await aufruf(tok);
    } finally {
      laeuft.current = false;
      // Der Token ist verbraucht, ob der Aufruf gelang oder nicht.
      const id = widget.current;
      if (id && window.turnstile) {
        try { window.turnstile.reset(id); } catch { /* Widget schon weg */ }
      }
    }
  }, []);

  // Callback-Ref statt useEffect: so entsteht das Widget, wenn das Feld
  // erscheint, auch wenn es nur in einem Zweig der Seite steht (/connect).
  const box = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    let weg = false;
    ladeTurnstile()
      .then(api => {
        if (weg) return;
        widget.current = api.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          action: aktion,
          language: sprache(),
          appearance: 'always',
          size: 'flexible',
          callback: (tok: string) => {
            setStoerung(false);
            const aufruf = offen.current;
            if (aufruf) {
              offen.current = null;
              setWartet(false);
              void einloesen(aufruf, tok);
            } else {
              token.current = tok;
            }
          },
          // Abgelaufene Tokens holt Turnstile selbst neu (refresh-expired: auto).
          'expired-callback': () => { token.current = null; },
          'timeout-callback': () => {
            token.current = null;
            offen.current = null;
            setWartet(false);
            setStoerung(true);
          },
          'error-callback': (code: unknown) => {
            console.warn('[turnstile] Prüfung gescheitert:', aktion, code);
            token.current = null;
            offen.current = null;
            setWartet(false);
            setStoerung(true);
            return true;
          },
        }) ?? null;
      })
      .catch(fehler => {
        if (weg) return;
        console.warn('[turnstile]', fehler);
        offen.current = null;
        setWartet(false);
        setStoerung(true);
      });
    return () => {
      weg = true;
      const id = widget.current;
      widget.current = null;
      token.current = null;
      offen.current = null;
      if (id && window.turnstile) {
        try { window.turnstile.remove(id); } catch { /* schon weg */ }
      }
    };
    // `runde` gehört dazu: ein neuer Wert baut das Widget neu auf.
  }, [aktion, einloesen, runde]);

  const ausfuehren = useCallback((aufruf: (captchaToken: string) => Promise<unknown>) => {
    if (laeuft.current) return;
    const tok = token.current;
    if (tok) { void einloesen(aufruf, tok); return; }
    // Noch kein Token: warten und dann von selbst absenden. Bei einer
    // Störung steht die Meldung schon da, und erst „Noch einmal prüfen“ hilft.
    if (stoerung) return;
    offen.current = aufruf;
    setWartet(true);
  }, [einloesen, stoerung]);

  const nochmal = () => {
    setStoerung(false);
    setRunde(r => r + 1);
  };

  const feld = (
    <>
      <div ref={box} className="mb-4 min-h-[65px]" />
      {stoerung && (
        <Hinweis art="schlecht">
          {t(
            'Die Sicherheitsprüfung hat nicht geklappt. Lass sie noch einmal laufen; hilft das nicht, lade die Seite neu.',
            'The security check did not work. Run it again; if that does not help, reload the page.',
          )}{' '}
          <button
            type="button" onClick={nochmal}
            className="cursor-pointer border-0 bg-transparent p-0 text-sm text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke"
          >
            {t('Noch einmal prüfen', 'Check again')}
          </button>
        </Hinweis>
      )}
    </>
  );

  return { feld, wartet, ausfuehren };
}
